import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import UserProfileHeader from './UserProfileHeader';
import "../Styles/UserManagement.css";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [originalUsers, setOriginalUsers] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const role = localStorage.getItem('role');

    const roleMap = {
        1: 'Admin',
        2: 'First Approver',
        3: 'Second Approver',
        4: 'YubiKey Manager',
        5: 'Logistics Administrator',
        6: 'Technician',
    };

    const constrainedRoles = new Set([
        'First Approver',
        'Second Approver',
        'YubiKey Manager',
        'Logistics Administrator'
    ]);

    const reverseRoleMap = Object.fromEntries(Object.entries(roleMap).map(([k, v]) => [v, Number(k)]));

    const rolesChanged = (current, original) => {
        if (current.length !== original.length) return true;
        const byEmail = Object.fromEntries(original.map(u => [u.email, u.role]));
        return current.some(u => byEmail[u.email] !== u.role);
    };

    const formatFetchedUsers = (users) => {
        return users.map(user => ({
            ...user,
            role: roleMap[user.role] || user.role
        }));
    };

    const fetchUsers = () => {
        Axios.post(`/get_users`).then((response) => {
            if (response.data.success) {
                const formatted = formatFetchedUsers(response.data.data);
                setUsers(formatted);
                setOriginalUsers(formatted);
                setIsDirty(false);
            } else {
                toast.error("Failed to get all users.", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
        });
    };

    const handleRoleChange = (userEmail, newRole) => {
        setUsers((prevUsers) => {
            // find current value so we can compare / revert
            const currentUser = prevUsers.find(u => u.email === userEmail);
            if (!currentUser) return prevUsers;

            // Allow unlimited technicians
            if (constrainedRoles.has(newRole)) {
                const alreadyTaken = prevUsers.some(
                    (u) => u.role === newRole && u.email !== userEmail
                );
                if (alreadyTaken) {
                    toast.warn(`Only one ${newRole} is allowed.`, {
                        position: "top-right",
                        autoClose: 4000,
                        theme: "colored",
                    });
                    // Do NOT change anything; controlled <select> will stay on previous value
                    return prevUsers;
                }
            }

            // Safe to apply the change
            const next = prevUsers.map(u =>
                u.email === userEmail ? { ...u, role: newRole } : u
            );
            setIsDirty(rolesChanged(next, originalUsers));
            return next;
        });
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleSelectUser = (userEmail) => {
        setSelectedUsers((prev) => {
            if (prev.includes(userEmail)) {
                return prev.filter((email) => email !== userEmail);
            } else {
                return [...prev, userEmail];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map(user => user.email));
        }
        setSelectAll(!selectAll);
    };

    const handleDeleteUsers = () => {
        Axios.post(`/delete_velox_users`, { emails: selectedUsers }).then((response) => {
            if (response.data.success) {
                toast.success(`${response.data.msg}`, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
            fetchUsers();
            setSelectedUsers([]);
            setOpenDialog(false);;
        });
    };

    const handleSave = async () => {
        try {
            // Build minimal payload (email + role). If backend needs IDs, map names -> IDs.
            const payload = users.map(u => ({
                email: u.email,
                role: reverseRoleMap[u.role] ?? u.role  // send ID if known, else name
            }));

            const res = await Axios.post('/update_user_roles', { users: payload, operator_email: (localStorage.getItem("userEmail") || "").trim() });
            if (res.data?.success) {
                toast.success(res.data.msg || 'Roles saved.', { position: 'top-right' });
                // snap new “original” to current; clear dirty
                setOriginalUsers(users);
                setIsDirty(false);
                // (Optional) refetch to normalize from server:
                // fetchUsers();
            } else {
                toast.error(res.data?.msg || 'Failed to save roles.', { position: 'top-right' });
            }
        } catch (err) {
            toast.error('Network or server error while saving.', { position: 'top-right' });
            console.error(err);
        }
    };

    return (
        <div className="dashboard">
            <UserProfileHeader isAdmin={true} />

            <div className="all-users-card">
                <h1 className="Title-alluser">VELOX Users</h1>

                <div className="button-row">
                    {/* <button onClick={fetchUsers} className="refresh-button">Refresh</button> */}
                    <button onClick={fetchUsers} className="refresh-button">Refresh</button>
                    <button onClick={handleSave} className="save-button" disabled={!isDirty}>Save</button>
                    <button
                        onClick={() => setOpenDialog(true)}
                        className="delete-button"
                        disabled={selectedUsers.length === 0}
                    >
                        Delete
                    </button>
                </div>

                <div className="table-container">
                    <table className="styled-table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th>Order</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email</th>
                                <th>YubiKey Serial Number</th>
                                <th>Role</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user, index) => (
                                <tr key={user.email}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(user.email)}
                                            onChange={() => handleSelectUser(user.email)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td>{user.first_name}</td>
                                    <td>{user.last_name}</td>
                                    <td>{user.email}</td>
                                    <td>{user.key_serial_number}</td>
                                    <td>
                                        {role != 1 ? (
                                            <span className="dropdown-readonly">{user.role}</span>
                                        ) : (
                                            <select
                                                className="role-dropdown"
                                                value={user.role}
                                                onChange={(e) => handleRoleChange(user.email, e.target.value)}
                                            >
                                                <option value="Admin">Admin</option>
                                                <option value="First Approver">First Approver</option>
                                                <option value="Second Approver">Second Approver</option>
                                                <option value="YubiKey Manager">YubiKey Manager</option>
                                                <option value="Logistics Administrator">Logistics Administrator</option>
                                                <option value="Technician">Technician</option>
                                            </select>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Delete Users"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Do you want to delete these users?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteUsers} color="primary">Yes</Button>
                        <Button onClick={() => setOpenDialog(false)} color="secondary" autoFocus>No</Button>
                    </DialogActions>
                </Dialog>
            </div>

            <ToastContainer />
        </div>
    );

};

export default UserManagement;
